import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import CountStrip from './CountStrip';
import { EDM_UI, canUseClientsSource, edmErrorMessage } from '../../../../utils/edm';
import type {
  EdmAudience,
  EdmAudienceCountPage,
  EdmAudienceCounters,
  EdmFreezeCounters,
  EdmSegment,
  EdmTag,
} from '../../../../hooks/useEdmCampaigns';
import TagPicker from './TagPicker';
import BrokerPicker from './BrokerPicker';
import type { EdmBrokerOption } from './BrokerPicker';
import SegmentPicker from './SegmentPicker';

/** Live count debounce: one countAudience per pause in editing. */
export const EDM_COUNT_DEBOUNCE_MS = 600;

/** Paged count guard (edm_content): ≤2,000 source docs per page, so 500 pages is a 1M-record book. */
export const EDM_COUNT_MAX_PAGES = 500;

const NOT_AVAILABLE = 'The recipient count is not available right now.';

const anySource = (a: EdmAudience): boolean => a.includeLeads || a.includeClients || a.includeContacts;

interface SourceRow {
  key: keyof Pick<EdmAudience, 'includeLeads' | 'includeClients' | 'includeContacts'>;
  label: string;
}

const AudiencePanel: React.FC<{
  audience: EdmAudience;
  onChange: (a: EdmAudience) => void;
  disabled?: boolean;
  role?: string;
  canImport: boolean;
  /** "Company-wide." or "Limited to your offices: …". */
  scopeLine: string;
  counters: EdmFreezeCounters | null;
  brokers: EdmBrokerOption[];
  brokersLoading: boolean;
  /** The audience as the server will see it (role-stripped), for the count and segments. */
  effectiveAudience: EdmAudience;
  countAudience: (a: EdmAudience) => Promise<{ counters: EdmAudienceCounters }>;
  listTags: () => Promise<{ tags: EdmTag[] }>;
  listSegments: () => Promise<{ segments: EdmSegment[] }>;
  saveSegment: (params: { name: string; spec: EdmAudience }) => Promise<{ id: string }>;
  deleteSegment: (segmentId: string) => Promise<unknown>;
  /** edm_content: offer "Apply tags to leads and clients too" and count page by page. Absent = Phase 1c. */
  contentEnabled?: boolean;
  /** edm_content: one page of the paged count (used only with contentEnabled). */
  countAudiencePage?: (
    a: EdmAudience,
    page: { cursor: string; jobId?: string | null },
  ) => Promise<EdmAudienceCountPage>;
}> = ({
  audience,
  onChange,
  disabled,
  role,
  canImport,
  scopeLine,
  counters,
  brokers,
  brokersLoading,
  effectiveAudience,
  countAudience,
  listTags,
  listSegments,
  saveSegment,
  deleteSegment,
  contentEnabled,
  countAudiencePage,
}) => {
  // ----- live count (draft only; a frozen campaign shows its freeze counters)
  const [live, setLive] = useState<EdmAudienceCounters | null>(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState<string | null>(null);
  const countRef = useRef(countAudience);
  countRef.current = countAudience;
  const pageRef = useRef(countAudiencePage);
  pageRef.current = countAudiencePage;
  const paged = contentEnabled === true && countAudiencePage !== undefined;
  // Paged count progress: recipients found in the pages counted so far.
  const [progress, setProgress] = useState<number | null>(null);
  const seqRef = useRef(0);
  const specKey = JSON.stringify(effectiveAudience);
  const countable = !disabled && anySource(effectiveAudience);
  useEffect(() => {
    const seq = ++seqRef.current;
    if (!countable) {
      setLive(null);
      setLiveError(null);
      setLiveLoading(false);
      return undefined;
    }
    setLiveLoading(true);
    const spec = JSON.parse(specKey) as EdmAudience;
    if (paged) {
      setProgress(null);
      const t = setTimeout(() => {
        const run = async () => {
          let cursor = '';
          let jobId: string | null = null;
          for (let pages = 1; ; pages += 1) {
            const page = pageRef.current;
            if (!page) throw new Error(NOT_AVAILABLE);
            const res: EdmAudienceCountPage = await page(spec, { cursor, jobId });
            if (seq !== seqRef.current) return;
            const c = res?.counters;
            if (!c || typeof c.recipients !== 'number') throw new Error(NOT_AVAILABLE);
            if (res.done) {
              setLive(c);
              setLiveError(null);
              return;
            }
            if (!res.cursor || pages >= EDM_COUNT_MAX_PAGES) throw new Error(NOT_AVAILABLE);
            setProgress(c.recipients);
            cursor = res.cursor.position;
            jobId = res.cursor.jobId;
          }
        };
        run()
          .catch((err) => {
            if (seq !== seqRef.current) return;
            setLive(null);
            setLiveError(err instanceof Error && err.message === NOT_AVAILABLE ? NOT_AVAILABLE : edmErrorMessage(err));
          })
          .finally(() => {
            if (seq === seqRef.current) {
              setLiveLoading(false);
              setProgress(null);
            }
          });
      }, EDM_COUNT_DEBOUNCE_MS);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      countRef
        .current(spec)
        .then((res) => {
          if (seq !== seqRef.current) return;
          const c = res?.counters;
          if (c && typeof c.recipients === 'number') {
            setLive(c);
            setLiveError(null);
          } else {
            setLive(null);
            setLiveError(NOT_AVAILABLE);
          }
        })
        .catch((err) => {
          if (seq !== seqRef.current) return;
          setLive(null);
          setLiveError(edmErrorMessage(err));
        })
        .finally(() => {
          if (seq === seqRef.current) setLiveLoading(false);
        });
    }, EDM_COUNT_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [countable, specKey, paged]);
  useEffect(
    () => () => {
      // Unmount: no late result may set state.
      seqRef.current += 1;
    },
    [],
  );

  const applySegment = (spec: EdmAudience) =>
    onChange({
      includeLeads: spec?.includeLeads === true,
      includeClients: canUseClientsSource(role) && spec?.includeClients === true,
      includeContacts: canImport && spec?.includeContacts === true,
      contactTags: spec?.contactTags && spec.contactTags.length > 0 ? spec.contactTags : null,
      brokerUids: spec?.brokerUids && spec.brokerUids.length > 0 ? spec.brokerUids : null,
      ...(contentEnabled ? { tagsApplyTo: spec?.tagsApplyTo === 'all' ? ('all' as const) : null } : {}),
    });

  // Marketing never sees the clients row at all (not merely disabled).
  const rows: SourceRow[] = [
    { key: 'includeLeads', label: 'Leads who ticked the marketing consent box on your website form' },
    ...(canUseClientsSource(role)
      ? [{ key: 'includeClients' as const, label: 'Current clients (active loan, commission in the last 12 months)' }]
      : []),
    ...(canImport ? [{ key: 'includeContacts' as const, label: 'Imported contacts' }] : []),
  ];

  return (
    <div className={EDM_UI.card}>
      <h2 className={EDM_UI.heading}>
        <Users className={EDM_UI.headingIcon} /> Audience
      </h2>
      <div className="space-y-3">
        {rows.map((r) => (
          <label key={r.key} className="flex items-start gap-3 text-sm text-gray-200">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              checked={audience[r.key]}
              disabled={disabled}
              onChange={(e) => onChange({ ...audience, [r.key]: e.target.checked })}
            />
            <span>
              {r.label}
              {r.key === 'includeContacts' && (
                <>
                  {' '}
                  <Link to="/leads/edm/import" className="text-blue-300 underline hover:text-blue-200">
                    Import contacts
                  </Link>
                </>
              )}
            </span>
          </label>
        ))}
      </div>
      {canImport && (
        <TagPicker
          selected={audience.contactTags}
          onChange={(contactTags) => onChange({ ...audience, contactTags })}
          listTags={listTags}
          disabled={disabled}
          {...(contentEnabled ? { appliesToAll: audience.tagsApplyTo === 'all' } : {})}
        />
      )}
      {!canImport && contentEnabled && audience.tagsApplyTo === 'all' && (audience.contactTags ?? []).length > 0 && (
        <p className={EDM_UI.infoBanner} data-testid="edm-tags-apply-note">
          Leads and clients are limited to people tagged {(audience.contactTags ?? []).join(', ')}. A company-wide admin
          set this filter; ask them to change it.
        </p>
      )}
      {canImport && contentEnabled && (
        <label className="flex items-start gap-3 text-sm text-gray-200">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4"
            checked={audience.tagsApplyTo === 'all'}
            disabled={disabled}
            onChange={(e) => onChange({ ...audience, tagsApplyTo: e.target.checked ? 'all' : null })}
          />
          <span>
            Apply tags to leads and clients too
            <span className="block text-xs text-gray-400">
              Leads and clients are included only when they carry one of the chosen tags. With no tags chosen, nobody is
              filtered.
            </span>
          </span>
        </label>
      )}
      <BrokerPicker
        brokers={brokers}
        loading={brokersLoading}
        selected={audience.brokerUids}
        onChange={(brokerUids) => onChange({ ...audience, brokerUids })}
        disabled={disabled}
      />
      <SegmentPicker
        audience={effectiveAudience}
        canSaveCurrent={anySource(effectiveAudience)}
        onApply={applySegment}
        listSegments={listSegments}
        saveSegment={saveSegment}
        deleteSegment={deleteSegment}
        disabled={disabled}
      />
      {countable && (
        <div className={EDM_UI.infoBanner} data-testid="edm-live-count" aria-live="polite">
          {liveError ? (
            <span className="text-red-300">{liveError}</span>
          ) : liveLoading || live === null ? (
            <span>
              Counting recipients…
              {progress !== null && ` ${progress.toLocaleString('en-AU')} found so far.`}
            </span>
          ) : (
            <div className="space-y-1">
              <p>
                <strong>{live.recipients.toLocaleString('en-AU')}</strong> recipients right now (counted again when you
                freeze the list).
              </p>
              <p className="text-xs text-blue-200/80">
                {live.duplicates.toLocaleString('en-AU')} duplicates removed · {live.suppressed.toLocaleString('en-AU')}{' '}
                suppressed · {live.invalidEmail.toLocaleString('en-AU')} invalid email ·{' '}
                {(live.excludedUnverifiedConsent ?? 0).toLocaleString('en-AU')} without verified consent
              </p>
              {(live.brokerUnmatched ?? 0) > 0 && (
                <p className="text-xs text-amber-300">
                  {(live.brokerUnmatched ?? 0).toLocaleString('en-AU')} selected brokers are no longer active and were
                  ignored.
                </p>
              )}
            </div>
          )}
        </div>
      )}
      <p className="text-xs text-gray-400">
        Each recipient is checked again at send: anyone who unsubscribes, withdraws consent, is erased, or leaves your
        offices before their email goes out is skipped.
      </p>
      <p className="text-xs text-gray-400">{scopeLine}</p>
      {counters && (
        <>
          <CountStrip
            label="Audience counts"
            items={[
              { label: 'Recipients', value: counters.recipients },
              { label: 'Duplicates removed', value: counters.duplicates },
              { label: 'Suppressed', value: counters.suppressed },
              { label: 'Invalid email', value: counters.invalidEmail },
            ]}
          />
          {counters.missingFirstName > 0 && (
            <p className="text-xs text-gray-400">
              {counters.missingFirstName.toLocaleString('en-AU')} recipients have no first name; {'{{firstName}}'} will be
              blank for them.
            </p>
          )}
        </>
      )}
      <p className="text-xs text-gray-500">Recipient lists are not shown or downloadable.</p>
    </div>
  );
};

export default AudiencePanel;
